import React from 'react';
import PropTypes from 'prop-types';
import { BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { TableSectionSkeleton } from './Skeletons';
import { formatDate, getProgressColor } from '../utils/formatters';
import { STATUS_BADGE_CONFIG, TABLE_CONFIG } from '../utils/constants';

const PAGE_SIZE = 10;

export default function CourseTable({ 
  data, 
  loading, 
  currentPage,
  totalCount,
  onPageChange 
}) {
  if (loading) return <TableSectionSkeleton cols={TABLE_CONFIG.COURSE.cols} />;

  if (!data || data.length === 0) {
    return (
      <p className="px-6 py-8 text-center text-sm text-gray-400">
        No courses enrolled yet.
      </p>
    );
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const renderStatusBadge = (status) => {
    const config = STATUS_BADGE_CONFIG[status] || STATUS_BADGE_CONFIG.NOT_STARTED;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 ${config.bg} border ${config.border} ${config.text} rounded text-xs font-semibold`}>
        {config.icon} {config.label}
      </span>
    );
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      onPageChange(currentPage + 1);
    }
  };

  const startIndex = currentPage * PAGE_SIZE + 1;
  const endIndex = Math.min((currentPage + 1) * PAGE_SIZE, totalCount);

  return (
    <div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            {TABLE_CONFIG.COURSE.headers.map((h) => (
              <th
                key={h}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {data.map((item) => {
            const progress = item.progressPercentage || 0;
            const status = item.status?.toUpperCase();

            return (
              <tr key={item.planId} className="hover:bg-gray-50">
                <td className="px-6 py-3 text-sm">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="text-blue-600">{item.courseName}</span>
                  </div>
                </td>
                <td className="px-6 py-3 text-sm text-gray-600">{item.format}</td>
                <td className="px-6 py-3 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                          width: `${progress}%`,
                          backgroundColor: getProgressColor(status),
                        }}
                      />
                    </div>
                    <span className="text-gray-700 text-xs font-semibold w-10 text-right">{progress}%</span>
                  </div>
                </td>
                <td className="px-6 py-3 text-sm">{renderStatusBadge(status)}</td>
                <td className="px-6 py-3 text-sm text-gray-600">
                  {status === 'COMPLETED' && progress === 100 ? formatDate(item.completionDate) : '—'}
                </td>
                <td className="px-6 py-3 text-sm">
                  {item.certificateUrl ? (
                    <a
                      href={item.certificateUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 text-amber-700 rounded text-xs font-medium hover:underline"
                    >
                      View Certificate
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Server-side Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          <div className="text-sm text-gray-600">
            Showing {startIndex} to {endIndex} of {totalCount} courses
          </div>
          <div className="flex gap-2 items-center">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className="p-1.5 text-gray-600 border border-gray-300 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="px-4 py-1.5 text-sm font-medium text-gray-700">
              {currentPage + 1} / {totalPages}
            </div>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
              className="p-1.5 text-gray-600 border border-gray-300 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

CourseTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

